import BorderColorIcon from '@mui/icons-material/BorderColor';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DateRangeIcon from '@mui/icons-material/DateRange';
import DescriptionIcon from '@mui/icons-material/Description';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@apollo/client';
import {
  ContentState,
  convertFromRaw,
  convertToRaw,
  RawDraftContentState,
} from 'draft-js';
import { DraftWithBlockCounter } from '../../../components/Draft/DraftWithBlockCounter';
import { actions as draftActions } from '../../../components/Draft/DraftActions';
import {
  NotificationTypeEnum,
  useNotification,
} from '@prismamedia/one-components';
import { cloneDeep, isEqual } from 'lodash';
import {
  ChangeEvent,
  Dispatch,
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactGA from 'react-ga4';
import {
  GENERATE_ARTICLE_SUMMARY,
  GenerateArticleSummaryMutation,
  GenerateArticleSummaryMutationVariables,
} from './generateArticleSummary.web.graphql';
import {
  GET_ARTICLE_SUMMARY,
  GetArticleSummaryQuery,
  GetArticleSummaryQueryVariables,
} from './getArticleSummary.web.graphql';
import {
  UPDATE_ARTICLE_SUMMARY,
  UpdateArticleSummaryMutation,
  UpdateArticleSummaryMutationVariables,
} from './updateArticleSummary.web.graphql';
import {
  UPSERT_ARTICLE_SUMMARY,
  UpsertArticleSummaryMutation,
  UpsertArticleSummaryMutationVariables,
} from './upsertArticleSummary.web.graphql';
import {
  ArticleFormat,
  ArticleStatus,
  ArticleSummaryStatus,
  BrandKey,
  CategoryFormat,
  CategoryStatus,
  GetBrandCategoriesQuery,
  GetBrandCategoriesQueryVariables,
} from '../../../__generated__/queries-web';
import { client } from '../../../apollo';
import { Suggest } from '../../../components/Suggest';
import { getTitleFromBrandKey } from '../../../utils/brands';
import { formatDate } from '../../../utils/dateUtils';
import { GET_BRAND_CATEGORIES } from '../../SlideshowEdit/CategorySelector/getBrandCategories.web.graphql';
import { SourceInput } from '../ArticleEditor/SourceInput';
import { ArticleEditAction, ArticleState, actions } from '../reducer';
import { LinksContent } from './LinksContent';
import { LinksToTopic } from './LinksToTopic';
import { PlanningSection } from './PlanningSection';
import { Qualifiers } from './Qualifiers';
import { useStyles } from './styles';

const TABS_SHOW_LABELS_MIN_WIDTH_PX = 360;

export interface DrawerContentHandle {
  saveSummary: () => void;
}

type DrawerContentProps = {
  sidebarWidth: number;
  subjectId?: string;
  subjectTitle?: string;
  renderInput: (inputName: string) => boolean;
  dispatch: Dispatch<ArticleEditAction>;
} & Pick<
  ArticleState,
  | 'publishedAt'
  | 'categories'
  | 'tags'
  | 'authors'
  | 'source'
  | 'events'
  | 'status'
  | 'slug'
  | 'brandKey'
  | 'qualifiers'
  | 'guides'
  | 'channels'
  | 'lead'
  | 'title'
  | 'body'
  | 'id'
  | 'format'
  | 'machineTags'
  | 'unpublishAt'
>;

export const DrawerContent = memo(
  forwardRef<DrawerContentHandle, DrawerContentProps>(
    (
      {
        publishedAt,
        unpublishAt,
        source,
        status,
        renderInput,
        brandKey,
        qualifiers,
        channels,
        dispatch,
        title,
        format,
        body,
        machineTags,
        id,
        sidebarWidth,
      },
      ref,
    ) => {
      const classes = useStyles();
      const [tabValue, setTabValue] = useState(0);
      const [summaryId, setSummaryId] = useState<string | null>(null);
      const [summaryDraft, setSummaryDraft] = useState<RawDraftContentState>(
        () => convertToRaw(ContentState.createFromText('')),
      );
      const [summaryStatus, setSummaryStatus] = useState<
        'generated' | 'validated'
      >('generated');
      const { pushNotification } = useNotification();

      const summaryArticleIdRef = useRef<string | undefined>(undefined);
      const summarySaveBaselineRef = useRef<RawDraftContentState | null>(null);
      const summaryDraftRef = useRef(summaryDraft);

      const handleSummaryDraftChange = useCallback(
        (next: RawDraftContentState) => {
          // eslint-disable-next-line immutable/no-mutation -- ref holds latest draft for save before React re-renders
          summaryDraftRef.current = next;
          setSummaryDraft(next);
        },
        [],
      );

      const summaryQueryVariables = useMemo(
        () => (id ? { where: { article: { id } } } : undefined),
        [id],
      );

      const {
        data: existingSummaryData,
        loading: summaryQueryLoading,
      } = useQuery<GetArticleSummaryQuery, GetArticleSummaryQueryVariables>(
        GET_ARTICLE_SUMMARY,
        {
          skip: !id,
          variables: summaryQueryVariables,
          fetchPolicy: 'cache-and-network',
        },
      );

      useEffect(() => {
        const existing = existingSummaryData?.articleSummaryIfExists;
        if (!existing || summaryId !== null) return;
        setSummaryId(existing.id);
        const draft = existing.body
          ? (existing.body as RawDraftContentState)
          : convertToRaw(ContentState.createFromText(''));
        const summaryStatusFromApi: 'generated' | 'validated' =
          existing.status === ArticleSummaryStatus.Published
            ? 'validated'
            : 'generated';
        // eslint-disable-next-line immutable/no-mutation -- keep ref aligned with hydrated server draft
        summaryDraftRef.current = draft;
        setSummaryDraft(draft);
        setSummaryStatus(summaryStatusFromApi);
      }, [existingSummaryData, summaryId]);

      useEffect(() => {
        if (!id) return;
        if (summaryArticleIdRef.current !== id) {
          // eslint-disable-next-line immutable/no-mutation -- ref tracks article id for baseline reset
          summaryArticleIdRef.current = id;
          // eslint-disable-next-line immutable/no-mutation -- ref baseline; reset when article changes
          summarySaveBaselineRef.current = null;
        }
        if (summaryQueryLoading || existingSummaryData === undefined) return;
        if (summarySaveBaselineRef.current !== null) return;
        const existing = existingSummaryData.articleSummaryIfExists;
        const bodyFromServer = existing?.body
          ? (existing.body as RawDraftContentState)
          : convertToRaw(ContentState.createFromText(''));
        // eslint-disable-next-line immutable/no-mutation -- ref stores initial summary body for save comparison
        summarySaveBaselineRef.current = cloneDeep(bodyFromServer);
      }, [id, summaryQueryLoading, existingSummaryData]);

      const bodyToDisplayText = useCallback((value: unknown): string => {
        if (typeof value === 'string') return value;
        if (
          value &&
          typeof value === 'object' &&
          'blocks' in value &&
          Array.isArray((value as RawDraftContentState).blocks)
        ) {
          try {
            return convertFromRaw(value as RawDraftContentState).getPlainText();
          } catch {
            return JSON.stringify(value);
          }
        }
        return value != null ? String(value) : '';
      }, []);

      const summaryDraftPlainTrimmed = useCallback(
        (raw: RawDraftContentState): string => {
          try {
            return convertFromRaw(raw).getPlainText().trim();
          } catch {
            return '';
          }
        },
        [],
      );

      const isSummaryBodyUnchanged = useCallback(
        (draft: RawDraftContentState, baseline: RawDraftContentState) =>
          isEqual(draft, baseline) ||
          summaryDraftPlainTrimmed(draft) ===
            summaryDraftPlainTrimmed(baseline),
        [summaryDraftPlainTrimmed],
      );

      const [
        generateSummary,
        { loading: generateSummaryLoading },
      ] = useMutation<
        GenerateArticleSummaryMutation,
        GenerateArticleSummaryMutationVariables
      >(GENERATE_ARTICLE_SUMMARY, {
        onCompleted: (data) => {
          if (summaryQueryVariables) {
            client.writeQuery<GetArticleSummaryQuery>({
              query: GET_ARTICLE_SUMMARY,
              variables: summaryQueryVariables,
              data: {
                articleSummaryIfExists: data.generateArticleSummary,
              },
            });
          }
          setSummaryId(data.generateArticleSummary.id);
          const generatedBody = data.generateArticleSummary?.body;
          const rawDraft =
            generatedBody != null &&
            typeof generatedBody === 'object' &&
            'blocks' in generatedBody
              ? (generatedBody as RawDraftContentState)
              : convertToRaw(
                  ContentState.createFromText(
                    bodyToDisplayText(generatedBody ?? ''),
                  ),
                );
          // eslint-disable-next-line immutable/no-mutation -- ref aligned with generated draft before paint
          summaryDraftRef.current = rawDraft;
          setSummaryDraft(rawDraft);
          setSummaryStatus('generated');
          // eslint-disable-next-line immutable/no-mutation -- ref baseline after generate
          summarySaveBaselineRef.current = cloneDeep(rawDraft);
          pushNotification({
            type: NotificationTypeEnum.success,
            message: 'Résumé généré avec succès.',
          });
        },
        onError: (error) => {
          pushNotification({
            type: NotificationTypeEnum.error,
            message: error.message ?? 'Erreur lors de la génération du résumé.',
          });
        },
      });

      const [upsertSummary] = useMutation<
        UpsertArticleSummaryMutation,
        UpsertArticleSummaryMutationVariables
      >(UPSERT_ARTICLE_SUMMARY, {
        onCompleted: (data) => setSummaryId(data.upsertArticleSummary.id),
        onError: (error) => {
          pushNotification({
            type: NotificationTypeEnum.error,
            message: error.message ?? 'Erreur lors de la sauvegarde du résumé.',
          });
        },
      });

      const buildUpsertVars = useCallback((): UpsertArticleSummaryMutationVariables | null => {
        if (!id) return null;
        const summaryBody = summaryDraftRef.current;
        return {
          where: { article: { id } },
          create: {
            article: { connect: { id } },
            body: summaryBody,
            status: ArticleSummaryStatus.Draft,
          },
          update: {
            body: summaryBody,
          },
        };
      }, [id]);

      const [
        updateSummaryStatus,
        { loading: statusUpdateLoading },
      ] = useMutation<
        UpdateArticleSummaryMutation,
        UpdateArticleSummaryMutationVariables
      >(UPDATE_ARTICLE_SUMMARY, {
        onError: (error) => {
          pushNotification({
            type: NotificationTypeEnum.error,
            message:
              error.message ??
              'Erreur lors de la mise à jour du statut du résumé.',
          });
        },
      });

      const handleSummaryValidationToggle = useCallback(
        (_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
          if (!id || !summaryId) return;
          if (checked) {
            ReactGA.event({
              category: 'Article résumé',
              action: 'Article résumé — validation',
              label: id,
            });
          }
          updateSummaryStatus({
            variables: {
              where: { article: { id } },
              data: {
                status: checked
                  ? ArticleSummaryStatus.Published
                  : ArticleSummaryStatus.Draft,
              },
            },
            onCompleted: (data) => {
              const isPublished =
                data.updateArticleSummary.status ===
                ArticleSummaryStatus.Published;
              setSummaryStatus(isPublished ? 'validated' : 'generated');
              if (summaryQueryVariables) {
                const cached = client.readQuery<GetArticleSummaryQuery>({
                  query: GET_ARTICLE_SUMMARY,
                  variables: summaryQueryVariables,
                });
                if (cached?.articleSummaryIfExists) {
                  client.writeQuery<GetArticleSummaryQuery>({
                    query: GET_ARTICLE_SUMMARY,
                    variables: summaryQueryVariables,
                    data: {
                      articleSummaryIfExists: {
                        ...cached.articleSummaryIfExists,
                        status: data.updateArticleSummary.status,
                      },
                    },
                  });
                }
              }
            },
          });
        },
        [id, summaryId, summaryQueryVariables, updateSummaryStatus],
      );

      const handleSaveSummary = useCallback(() => {
        if (!id) return;
        const draft = summaryDraftRef.current;
        const baseline = summarySaveBaselineRef.current;
        const draftPlain = summaryDraftPlainTrimmed(draft);
        if (baseline === null) {
          if (draftPlain === '') return;
        } else if (isSummaryBodyUnchanged(draft, baseline)) {
          return;
        }
        const upsertVars = buildUpsertVars();
        if (!upsertVars) return;
        upsertSummary({
          variables: upsertVars,
          onCompleted: () => {
            // eslint-disable-next-line immutable/no-mutation -- ref baseline after successful upsert
            summarySaveBaselineRef.current = cloneDeep(summaryDraftRef.current);
          },
        });
      }, [
        id,
        upsertSummary,
        buildUpsertVars,
        summaryDraftPlainTrimmed,
        isSummaryBodyUnchanged,
      ]);

      useImperativeHandle(ref, () => ({ saveSummary: handleSaveSummary }), [
        handleSaveSummary,
      ]);

      const handleGenerateSummary = useCallback(async () => {
        if (!id) return;
        ReactGA.event({
          category: 'Article résumé',
          action: 'Article résumé — génération',
          label: id,
        });
        await generateSummary({ variables: { article: { id } } });
      }, [id, generateSummary]);

      const channelsCallback = useCallback(async () => {
        const { data: getBrandsData } = await client.query<
          GetBrandCategoriesQuery,
          GetBrandCategoriesQueryVariables
        >({
          query: GET_BRAND_CATEGORIES,
          variables: {
            brandKey: brandKey || BrandKey.CAP,
            format: CategoryFormat.Channel,
            status: CategoryStatus.Published,
          },
        });
        return getBrandsData.categories;
      }, [brandKey]);

      const scheduleDateComponent = renderInput('publishedAt') && (
        <TextField
          disabled={status === ArticleStatus.Published}
          fullWidth
          id="datetime-local-published"
          inputProps={{
            min: '1970-01-01T00:00',
            max: '2100-01-01T00:00',
          }}
          InputLabelProps={{ shrink: true }}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            dispatch(
              actions.updateField({
                field: 'publishedAt',
                value: e.currentTarget.value,
              }),
            );
          }}
          type="datetime-local"
          value={
            publishedAt
              ? formatDate(new Date(publishedAt), "yyyy-MM-dd'T'HH:mm")
              : ''
          }
          variant="standard"
        />
      );

      const unpublishDateComponent = renderInput('unpublishAt') && (
        <TextField
          fullWidth
          id="datetime-local-unpublish"
          inputProps={{
            min: formatDate(new Date(), "yyyy-MM-dd'T'HH:mm") || '',
            max: '2100-01-01T00:00',
          }}
          InputLabelProps={{ shrink: true }}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            dispatch(
              actions.updateField({
                field: 'unpublishAt',
                value: e.currentTarget.value,
              }),
            );
          }}
          type="datetime-local"
          value={
            unpublishAt
              ? formatDate(new Date(unpublishAt), "yyyy-MM-dd'T'HH:mm")
              : ''
          }
          variant="standard"
        />
      );

      if (format === ArticleFormat.Rich) {
        const isCompactTabs = sidebarWidth < TABS_SHOW_LABELS_MIN_WIDTH_PX;

        const tabSx = {
          fontWeight: 500,
          minHeight: 48,
          textTransform: 'none' as const,
          ...(isCompactTabs && { minWidth: 0, px: 0.5 }),
        };

        const isSummaryPublished = summaryStatus === 'validated';

        const resumeTabIcon =
          isCompactTabs && isSummaryPublished ? (
            <Box
              sx={{
                alignItems: 'center',
                display: 'inline-flex',
                flexDirection: 'row',
                gap: 0.25,
              }}
            >
              <DescriptionIcon />
              <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />
            </Box>
          ) : (
            <DescriptionIcon />
          );

        const resumeTabLabel = isCompactTabs ? undefined : (
          <Box
            component="span"
            sx={{
              alignItems: 'center',
              display: 'inline-flex',
              gap: 0.5,
            }}
          >
            Résumé
            {isSummaryPublished && (
              <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />
            )}
          </Box>
        );

        return (
          <Box
            sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
          >
            <Tabs
              value={tabValue}
              onChange={(_, v: number) => setTabValue(v)}
              variant="fullWidth"
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                minHeight: 48,
                '& .MuiTab-root': { minHeight: 48 },
                '& .Mui-selected': {
                  color: 'primary.main',
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: 'primary.main',
                },
              }}
            >
              <Tab
                aria-label="Plan — dates et programmation"
                icon={<DateRangeIcon />}
                iconPosition={isCompactTabs ? 'top' : 'start'}
                label={isCompactTabs ? undefined : 'Plan'}
                title={isCompactTabs ? 'Plan' : undefined}
                sx={tabSx}
              />
              <Tab
                aria-label={
                  isSummaryPublished ? 'Résumé — résumé publié' : 'Résumé'
                }
                icon={resumeTabIcon}
                iconPosition={isCompactTabs ? 'top' : 'start'}
                label={resumeTabLabel}
                title={isCompactTabs ? 'Résumé' : undefined}
                sx={tabSx}
              />
              <Tab
                aria-label="Liens"
                icon={<InsertLinkIcon />}
                iconPosition={isCompactTabs ? 'top' : 'start'}
                label={isCompactTabs ? undefined : 'Liens'}
                title={isCompactTabs ? 'Liens' : undefined}
                sx={tabSx}
              />
            </Tabs>
            <Box sx={{ flex: 1, overflow: 'auto', pt: 2 }}>
              {tabValue === 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    width: '100%',
                    px: 2,
                    pt: 3,
                    gap: 3,
                  }}
                >
                  <Box sx={{ width: '100%', maxWidth: 360 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                      Date de programmation
                    </Typography>
                    {scheduleDateComponent}
                  </Box>
                  <Box sx={{ width: '100%', maxWidth: 360 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                      Date de dépublication
                    </Typography>
                    {unpublishDateComponent}
                  </Box>
                  <Box sx={{ width: '100%' }}>
                    <PlanningSection
                      brandKey={brandKey}
                      currentArticleId={id}
                    />
                  </Box>
                </Box>
              )}
              {tabValue === 1 && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    py: 2,
                  }}
                >
                  <Box sx={{ px: 2 }}>
                    <Button
                      disabled={!id || generateSummaryLoading}
                      fullWidth
                      startIcon={
                        generateSummaryLoading ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <BorderColorIcon />
                        )
                      }
                      onClick={handleGenerateSummary}
                      variant="contained"
                      sx={{
                        textTransform: 'none',
                        py: 0.75,
                        fontSize: '0.9375rem',
                        fontWeight: 600,
                      }}
                    >
                      {summaryStatus === 'validated'
                        ? 'Regénérer'
                        : 'Générer le résumé'}
                    </Button>
                  </Box>
                  <DraftWithBlockCounter
                    actions={[draftActions.BOLD, draftActions.ITALIC]}
                    rawDraft={summaryDraft}
                    onChange={handleSummaryDraftChange}
                    placeholder="Résumé"
                    showBlockCounters={false}
                  />
                  <Box
                    sx={{
                      alignItems: 'center',
                      display: 'flex',
                      justifyContent: 'flex-end',
                      px: 2,
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={summaryStatus === 'validated'}
                          disabled={!summaryId || statusUpdateLoading}
                          onChange={handleSummaryValidationToggle}
                          sx={(theme) => ({
                            transform: 'scale(1)',
                            '& .MuiSwitch-track': {
                              backgroundColor: theme.palette.grey[400],
                              opacity: 1,
                            },
                            '& .MuiSwitch-thumb': {
                              backgroundColor: theme.palette.common.white,
                              border: `2px solid ${theme.palette.grey[500]}`,
                              boxSizing: 'border-box',
                            },
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              '& + .MuiSwitch-track': {
                                backgroundColor: '#16a34a',
                                opacity: 1,
                              },
                              '& .MuiSwitch-thumb': {
                                backgroundColor: theme.palette.common.white,
                                border: '2px solid #16a34a',
                              },
                            },
                          })}
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Valider le résumé
                        </Typography>
                      }
                      labelPlacement="start"
                      sx={{ m: 0 }}
                    />
                  </Box>
                </Box>
              )}
              {tabValue === 2 && (
                <>
                  <LinksContent
                    body={body}
                    title={title}
                    brandKey={brandKey}
                    dispatch={dispatch}
                    idArticle={id}
                  />
                  <LinksToTopic articleMachineTags={machineTags} />
                </>
              )}
            </Box>
          </Box>
        );
      }

      return (
        <div className={classes.grid}>
          <Typography variant="h6">{getTitleFromBrandKey(brandKey)}</Typography>
          {scheduleDateComponent}
          {renderInput('channel') && (
            <FormControl className={classes.formSuggest} variant="standard">
              <InputLabel shrink>Chaîne vidéo :</InputLabel>
              <Suggest
                selectedItems={channels.map(({ category }) => category)}
                placeholder="Lier à une chaine vidéos"
                suggestions={channelsCallback}
                multiple={false}
                isPrefetched
                onChange={(items) => {
                  dispatch(
                    actions.updateField({
                      field: 'channels',
                      value: items.map((item) => ({
                        category: { id: item.id, title: item.title },
                      })),
                    }),
                  );
                }}
              />
            </FormControl>
          )}

          {renderInput('source') && (
            <FormControl className={classes.formSuggest} variant="standard">
              <SourceInput
                brandKey={brandKey}
                source={source}
                dispatch={dispatch}
              />
            </FormControl>
          )}

          <Qualifiers
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            brandKey={brandKey!}
            qualifiers={qualifiers}
            articleFormat={format}
            onChange={(updatedQualifiers) => {
              dispatch(
                actions.updateField({
                  field: 'qualifiers',
                  value: updatedQualifiers,
                }),
              );
            }}
          />
        </div>
      );
    },
  ),
);
